import z from 'zod';
import type { TemplateForm, TemplateFormField } from '../../backend/src/schema';
import type { PracticeArea, ProspectMatterType } from '../enums';

export const HiddenContactFieldsSchema = z.object({
    firstId: z.boolean().optional(),
    secondId: z.boolean().optional(),
    middleName: z.boolean().optional(),
    homePhone: z.boolean().optional(),
    homeAddress: z.boolean().optional(),
    homeUnit: z.boolean().optional(),
    dateOfBirth: z.boolean().optional(),
    maritalStatus: z.boolean().optional(),
    citizenshipStatus: z.boolean().optional(),
    gender: z.boolean().optional(),
    preferredPronouns: z.boolean().optional(),
    occupation: z.boolean().optional(),
    employer: z.boolean().optional(),
    employerAddress: z.boolean().optional(),
    employerUnit: z.boolean().optional(),
    employerPhone: z.boolean().optional(),
});
export type HiddenContactFields = z.infer<typeof HiddenContactFieldsSchema>;

export const RequiredContactFieldsSchema = HiddenContactFieldsSchema;
export type RequiredContactFields = z.infer<typeof RequiredContactFieldsSchema>;

// Visual order must match ContactInfoSection.
export const CONTACT_FIELD_LABEL_KEYS = [
    'firstIdType',
    'firstIdFront',
    'firstIdBack',
    'firstIdSingle',
    'secondIdType',
    'secondIdFront',
    'secondIdBack',
    'secondIdSingle',
    'firstName',
    'middleName',
    'lastName',
    'email',
    'cellPhone',
    'homePhone',
    'homeAddress',
    'homeUnit',
    'dateOfBirth',
    'maritalStatus',
    'citizenshipStatus',
    'gender',
    'preferredPronouns',
    'occupation',
    'employer',
    'employerAddress',
    'employerUnit',
    'employerPhone',
] as const;
export type ContactFieldLabelKey = (typeof CONTACT_FIELD_LABEL_KEYS)[number];

export const ContactFieldLabelsSchema = z.object({
    firstIdType: z.string().optional(),
    firstIdFront: z.string().optional(),
    firstIdBack: z.string().optional(),
    firstIdSingle: z.string().optional(),
    secondIdType: z.string().optional(),
    secondIdFront: z.string().optional(),
    secondIdBack: z.string().optional(),
    secondIdSingle: z.string().optional(),
    firstName: z.string().optional(),
    middleName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    cellPhone: z.string().optional(),
    homePhone: z.string().optional(),
    homeAddress: z.string().optional(),
    homeUnit: z.string().optional(),
    dateOfBirth: z.string().optional(),
    maritalStatus: z.string().optional(),
    citizenshipStatus: z.string().optional(),
    gender: z.string().optional(),
    preferredPronouns: z.string().optional(),
    occupation: z.string().optional(),
    employer: z.string().optional(),
    employerAddress: z.string().optional(),
    employerUnit: z.string().optional(),
    employerPhone: z.string().optional(),
});
export type ContactFieldLabels = z.infer<typeof ContactFieldLabelsSchema>;

export function toSparseContactFieldLabels(
    labels: ContactFieldLabels | null | undefined,
): ContactFieldLabels | undefined {
    if (!labels) return undefined;

    const sparse: ContactFieldLabels = {};
    for (const key of CONTACT_FIELD_LABEL_KEYS) {
        const value = labels[key]?.trim();
        if (value) {
            sparse[key] = value;
        }
    }

    return Object.keys(sparse).length > 0 ? sparse : undefined;
}

export const TEMPLATE_FORM_LANGUAGES = ['en', 'fr'] as const;
export type TemplateFormLanguage = (typeof TEMPLATE_FORM_LANGUAGES)[number];

export interface TemplateFormMetadata {
    hiddenContactFields?: HiddenContactFields;
    requiredContactFields?: RequiredContactFields;
    contactFieldLabels?: ContactFieldLabels;
    contactSectionDescription?: string | null;
    showFinancialSummary?: boolean;
    language?: TemplateFormLanguage;
}

export const TemplateFormMetadataSchema = z.object({
    hiddenContactFields: HiddenContactFieldsSchema.optional(),
    requiredContactFields: RequiredContactFieldsSchema.optional(),
    contactFieldLabels: ContactFieldLabelsSchema.optional(),
    contactSectionDescription: z.string().nullable().optional(),
    showFinancialSummary: z.boolean().optional(),
    language: z.enum(TEMPLATE_FORM_LANGUAGES).optional(),
});

export interface QuoteDetails {
    templateFormId: string;
    lawFirmId: number | null;
}

export interface TemplateFormResponse extends TemplateForm {
    practiceArea: PracticeArea;
    process: ProspectMatterType;
    isPostIntakeEnable: boolean;
    templateFormFields: TemplateFormField[];
}

export interface TemplateFormCodeResult {
    templateFormCode: string | null;
    lawFirmId: number | null;
    firstName: string;
    middleName: string | null;
    lastName: string;
    fullName: string;
    email: string;
    phoneNumber: string | null;
}

export interface FormGroup {
    name: string;
    displayOrder: number;
    description?: string | null;
}

export const FormGroupSchema = z.object({
    name: z.string(),
    displayOrder: z.number(),
    description: z.string().nullable().optional(),
});

export interface FormGroupRename {
    from: string;
    to: string;
}

/** A form's own properties, saved without its fields. Only the properties sent are changed */
export interface TemplateFormPropertiesUpdate {
    headerDescription?: string | null;
    footerTemplate?: string | null;
    metadata?: TemplateFormMetadata | null;
    formGroups?: FormGroup[] | null;
    /** Moves a group's questions to its new name. `formGroups` should already use the new name */
    renamedGroup?: FormGroupRename;
}

export const TemplateFormPropertiesUpdateSchema = z.object({
    headerDescription: z.string().nullable().optional(),
    footerTemplate: z.string().nullable().optional(),
    metadata: TemplateFormMetadataSchema.nullable().optional(),
    formGroups: z.array(FormGroupSchema).nullable().optional(),
    renamedGroup: z.object({ from: z.string().min(1), to: z.string().min(1) }).optional(),
});
